import React from "react";
import { useReveal } from "../hooks/useReveal";
import Countdown from "./Countdown";

const OVALS = "/assets/ovals.png";

const TEST_AREAS = [
  "Focus",
  "Presence",
  "Calmness under pressure",
  "Creativity",
  "Emotional regulation",
  "Workout engagement",
  "Aesthetic activation",
  "Overall state control",
];

const PARTICIPANT_PERKS = [
  "Early access to Protos",
  "Access to the private beta experience",
  "Daily usage guidance",
  "Performance tracking prompts",
  "Access to the founding participant group",
];

export default function BetaInvite({ onNotify }) {
  const head = useReveal();
  const left = useReveal();
  const right = useReveal();
  return (
    <section
      id="beta"
      data-testid="beta-section"
      className="section relative overflow-hidden"
    >
      <img
        src={OVALS}
        alt=""
        aria-hidden
        className="hidden lg:block pointer-events-none absolute top-1/2 -translate-y-1/2 right-[-15%] w-[55%] max-w-[700px] opacity-20 mix-blend-screen"
        style={{ animation: "pulseSoft 8s ease-in-out infinite" }}
      />

      <div className="container-x relative z-10">
        <div ref={head} className="reveal max-w-4xl mb-12 lg:mb-16">
          <div className="flex items-center gap-4 mb-8">
            <span className="tag tag-blue">
              <span className="tag-dot" /> 07 — The Experience
            </span>
            <span className="hidden sm:inline font-mono text-xs uppercase tracking-[0.3em] text-white/45">
              Private Beta · 14 Days
            </span>
          </div>
          <h2
            className="kinetic"
            style={{ fontSize: "clamp(3rem, 10vw, 9rem)", lineHeight: 0.9 }}
          >
            <span className="block gradient-text">THE PROTOS</span>
            <span className="block flex items-baseline gap-6 flex-wrap">
              <span className="outline-text-blue">100</span>
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{
                  background: "var(--blue-bright)",
                  boxShadow: "0 0 24px var(--blue-bright)",
                }}
              />
            </span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div ref={left} className="reveal lg:col-span-7 space-y-6 text-white/75 text-lg leading-relaxed">
            <p className="text-xl lg:text-2xl text-white font-light leading-snug">
              A private{" "}
              <span style={{ color: "var(--blue-bright)" }}>14-day beta experience</span>{" "}
              testing whether intentional resistance chewing can improve real
              state-control during the moments that matter.
            </p>

            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/45 pt-2">
              We're testing
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {TEST_AREAS.map((t, i) => (
                <div
                  key={i}
                  data-testid={`beta-test-${i}`}
                  className="flex items-center gap-3 py-2.5 px-4 border border-white/8 rounded-full"
                >
                  <span
                    className="font-mono text-[10px]"
                    style={{ color: "var(--blue-bright)" }}
                  >
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-white/85 text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div ref={right} className="reveal lg:col-span-5">
            <div className="card-dark p-7 lg:p-8 relative overflow-hidden">
              <div
                className="absolute -top-20 -right-20 w-44 h-44 rounded-full blur-3xl opacity-50"
                style={{
                  background: "radial-gradient(circle, rgba(31,179,255,0.4), transparent)",
                }}
              />
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/45 mb-5">
                Participants Receive
              </div>
              <ul className="space-y-3">
                {PARTICIPANT_PERKS.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`beta-perk-${i}`}
                    className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0"
                  >
                    <span
                      className="font-mono text-xs mt-1"
                      style={{ color: "var(--blue-bright)" }}
                    >
                      0{i + 1}
                    </span>
                    <span className="text-white/90 text-base leading-snug">{p}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid="beta-apply-btn"
                data-magnet
                onClick={onNotify}
                className="btn-primary mt-8 w-full justify-center"
              >
                <span>Apply to the Protos 100</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className="mt-5 flex justify-center">
                <Countdown
                  compact
                  testId="beta-countdown"
                  label="Closes in"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes pulseSoft { 0%,100% { opacity: 0.18; transform: translateY(-50%) scale(1);} 50% { opacity: 0.28; transform: translateY(-50%) scale(1.05);} }`}</style>
    </section>
  );
}
